// Segment rows for a whole portfolio, in one round trip.
//
// The company profile screen reads one company at a time; the exposure screen
// needs every holding at once, so it resolves ticker → cik in a single query
// and then pulls the geographic rows for all of them together. Two requests
// for a portfolio of any size.

import { useEffect, useState } from "react";
import { supabase } from "../lib/supabase";

const EMPTY = { segmentsBySymbol: {}, knownSymbols: new Set() };

async function loadPortfolioSegments(symbols) {
  if (!supabase || !symbols.length) return EMPTY;

  const { data: companies, error: cErr } = await supabase
    .from("companies")
    .select("cik, ticker")
    .in("ticker", symbols);
  if (cErr || !companies?.length) return EMPTY;

  const byCik = new Map(companies.map(c => [c.cik, c.ticker]));

  const { data: rows, error: sErr } = await supabase
    .from("company_segments")
    .select("cik, segment_type, segment_name, fiscal_year, revenue, source_url, filed_at")
    .in("cik", [...byCik.keys()]);
  if (sErr) return EMPTY;

  const segmentsBySymbol = {};
  // A company present in `companies` but with no segment rows still gets an
  // entry, because "we hold data on this company and it does not break revenue
  // down" is a different answer from "we hold nothing on it" — and the screen
  // shows them differently.
  for (const t of byCik.values()) segmentsBySymbol[t] = [];
  for (const r of rows || []) {
    const t = byCik.get(r.cik);
    if (t) segmentsBySymbol[t].push(r);
  }

  return { segmentsBySymbol, knownSymbols: new Set(byCik.values()) };
}

/** null while loading; never throws — a failed read is an empty data set. */
export function usePortfolioSegments(symbols) {
  const [state, setState] = useState(null);
  const key = [...symbols].sort().join(",");

  useEffect(() => {
    let alive = true;
    const list = key ? key.split(",") : [];
    if (!list.length) { setState(EMPTY); return; }
    setState(null);
    loadPortfolioSegments(list)
      .then(r => { if (alive) setState(r); })
      .catch(() => { if (alive) setState(EMPTY); });
    return () => { alive = false; };
  }, [key]);

  return state;
}

/** The filing behind the numbers — the newest one that contributed. */
export function exposureSource(segmentsBySymbol) {
  let newest = null;
  for (const rows of Object.values(segmentsBySymbol || {})) {
    for (const r of rows) {
      if (r.segment_type !== "geographic") continue;
      if (!r.filed_at) continue;
      if (!newest || r.filed_at > newest.filed_at) newest = r;
    }
  }
  return newest ? { filed: newest.filed_at, url: newest.source_url } : null;
}
